import { Form } from "antd"
import { useStore } from "jotai"
import { noop } from "lodash"
import { AnimatePresence, motion } from "motion/react"
import { useMemo } from "react"

import { classSessionAtoms, makeupAtoms, tryoutAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import { SFormHeader } from "#blocks/Form/formComponents"
import CopyClassContactInfo from "#components/CopyClassContactInfo"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { MakeupConstructor } from "#customTypes/composedTypes/makeupTypes"
import { TryoutConstructor } from "#customTypes/composedTypes/tryoutTypes"
import { type Uuid } from "#customTypes/globalTypes"
import { useGetSomeStudents } from "#hooks/jotai/useGetStudents"
import { useOneDoc, useSomeDocs } from "#hooks/jotai/useSubscription"
import { useGetSomeUsers } from "#hooks/newHooks/useGetUser"
import {
  formatMakeups,
  formatStudents,
  formatTeachers,
  formatTryouts,
} from "#utils/textFormatters"

import { type SessionInfo } from "./ClassSessionOverview"
import ClickableSessionNameList from "./ClickableSessionNameList"

const useOneClassSessionData = (sessionId: Uuid | null) => {
  const classSession = useOneDoc<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    sessionId
  )

  const {
    studentIds,
    makeupIds,
    plannedAbsenceIds,
    tryoutIds,
    teacherIds,
    assistantIds,
  } = classSession ?? {}

  const makeupsFromDb = useSomeDocs<MakeupConstructor>(
    makeupAtoms,
    DOC_TYPES.MAKEUPS,
    makeupIds
  )
  const plannedAbsencesFromDb = useSomeDocs<MakeupConstructor>(
    makeupAtoms,
    DOC_TYPES.MAKEUPS,
    plannedAbsenceIds
  )

  // Filter out student IDs that also appear in planned absences
  // TODO: This is a temporary fix until the db is fixed
  const filteredStudentIds = useMemo(() => {
    if (!studentIds || studentIds.length === 0) return []
    if (!plannedAbsencesFromDb || plannedAbsencesFromDb.length === 0)
      return studentIds

    const plannedAbsenceStudentIds = plannedAbsencesFromDb
      .map((makeup) => makeup.studentId)
      .filter(Boolean)

    return studentIds.filter(
      (studentId) => !plannedAbsenceStudentIds.includes(studentId)
    )
  }, [studentIds, plannedAbsencesFromDb])

  const studentsFromDb = useGetSomeStudents(filteredStudentIds)
  const tryoutsFromDb = useSomeDocs<TryoutConstructor>(
    tryoutAtoms,
    DOC_TYPES.TRYOUTS,
    tryoutIds
  )
  const teachersFromDb = useGetSomeUsers(teacherIds ?? [])
  const assistantsFromDb = useGetSomeStudents(assistantIds ?? [])

  const store = useStore()

  return {
    studentsForDisplay: formatStudents(studentsFromDb),
    makeupsForDisplay: formatMakeups(makeupsFromDb, store),
    plannedAbsencesForDisplay: formatMakeups(plannedAbsencesFromDb, store),
    tryoutsForDisplay: formatTryouts(tryoutsFromDb, store),
    teachersForDisplay: formatTeachers(teachersFromDb),
    assistantsForDisplay: formatStudents(assistantsFromDb),
  }
}

const ClassSessionDetails = ({
  sessionInfo,
  onSelectStudent,
  onSelectTeacher,
  onSelectAssistant,
}: {
  sessionInfo: SessionInfo
  onSelectStudent: (studentId: Uuid) => void
  onSelectTeacher: (teacherId: Uuid) => void
  onSelectAssistant: (assistantId: Uuid) => void
}) => {
  const form = Form.useFormInstance()
  const className = Form.useWatch<string>("name", form)
  const { project, fraction, daysToComplete, isOverflow } = sessionInfo
  const {
    studentsForDisplay,
    makeupsForDisplay,
    plannedAbsencesForDisplay,
    tryoutsForDisplay,
    teachersForDisplay,
    assistantsForDisplay,
  } = useOneClassSessionData(sessionInfo.id)

  const warningText = isOverflow
    ? "Warning: this curriculum has more project days than sessions."
    : !project
      ? "Warning: this curriculum has no project for this session"
      : ""

  const nameKey =
    project?.id && fraction
      ? `project-name-${project?.id}-${fraction}`
      : "project-name-unknown"
  const daysKey =
    project?.id && fraction && daysToComplete
      ? `project-days-${project?.id}-${fraction}-${daysToComplete}`
      : "project-days-unknown"

  const partsLabel =
    project?.id && fraction && daysToComplete
      ? `Part ${fraction}/${daysToComplete}`
      : "Unknown days to complete"

  return (
    <Flex col gap={16} style={{ minHeight: 150 }}>
      {/* Project details */}
      <Flex style={{ margin: "20px 0" }} gap={40}>
        <Flex col>
          <AnimatePresence mode="popLayout">
            <SFormHeader>
              Project:{" "}
              <motion.span
                key={nameKey}
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2 }}
                style={{ display: "inline-block" }}
              >
                {project?.name ?? "No project"}
              </motion.span>
            </SFormHeader>
          </AnimatePresence>
          <AnimatePresence mode="popLayout">
            <Flex>
              <motion.span
                key={daysKey}
                initial={{ opacity: 0, x: 5 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.1 }}
                style={{ display: "inline-block" }}
              >
                {partsLabel}
              </motion.span>
            </Flex>
          </AnimatePresence>
        </Flex>
        {warningText && (
          <SFormHeader style={{ color: "red" }}>{warningText}</SFormHeader>
        )}
      </Flex>

      {/* Attendance columns */}
      <Flex
        style={{
          border: "1px solid #ccc",
          borderRadius: "50%",
          width: "fit-content",
        }}
      >
        <CopyClassContactInfo
          sessionId={sessionInfo.id}
          className={className}
        />
      </Flex>

      <Flex gap={32}>
        <ClickableSessionNameList
          title={`Students (${studentsForDisplay?.length ?? 0})`}
          items={studentsForDisplay}
          onClick={onSelectStudent}
          isLink
        />
        <ClickableSessionNameList
          title={`Makeups (${makeupsForDisplay?.length ?? 0})`}
          items={makeupsForDisplay}
          onClick={noop}
        />
        <ClickableSessionNameList
          title={`Planned Absences (${plannedAbsencesForDisplay?.length ?? 0})`}
          items={plannedAbsencesForDisplay}
          onClick={noop}
        />
        <ClickableSessionNameList
          title={`Tryouts (${tryoutsForDisplay?.length ?? 0})`}
          items={tryoutsForDisplay}
          onClick={noop}
        />
        <ClickableSessionNameList
          title={`Teachers (${teachersForDisplay?.length ?? 0})`}
          items={teachersForDisplay}
          onClick={onSelectTeacher}
          isLink
        />
        <ClickableSessionNameList
          title={`TAs (${assistantsForDisplay?.length ?? 0})`}
          items={assistantsForDisplay}
          onClick={onSelectAssistant}
          isLink
        />
      </Flex>
    </Flex>
  )
}

export default ClassSessionDetails
